import { inferAdditionalFields } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { logger } from "@/logger";

/**
 * Authentication Client Configuration
 *
 * This file sets up the Better Auth client for our application.
 *
 * NOTE: This is a temporary setup. In the future, we plan to refactor the authentication system to
 * remove the dependency on the Better Auth client. The goal is to use our custom SessionProvider
 * exclusively for managing authentication state and actions.
 *
 * Current setup:
 *
 * - Uses createAuthClient from Better Auth library
 * - Configures the base URL for API requests
 * - Utilizes the inferAdditionalFields plugin to define extra user fields:
 *
 *   - Firstname (string)
 *   - Lastname (string)
 *   - Permissions (string array)
 * - Sends X-App-Slug header on ALL requests for multi-tenant support:
 *
 *   - Works in both web browsers and Capacitor apps (capacitor:// protocol)
 *   - Backend uses this to route users to correct whitelabel app
 *   - Configured via global fetchOptions.headers
 *
 * @todo Refactor authentication to use custom SessionProvider without Better Auth client
 */

// Better Auth strips the path from baseURL, only keeping the origin
// We need to intercept fetch calls and inject the path prefix
const getAuthConfig = () => {
  const backendURL = import.meta.env.VITE_BACKEND_URL;
  if (!backendURL) {
    return { baseURL: "/", pathPrefix: "" };
  }

  try {
    const url = new URL(backendURL);
    const pathPrefix = url.pathname.replace(/\/$/, ""); // e.g., "/version/1.1.3"
    const baseURL = url.origin; // e.g., "https://api.heyboomer.localhost"

    return { baseURL, pathPrefix };
  } catch {
    // If backendURL is a relative path, use it as-is
    return { baseURL: backendURL, pathPrefix: "" };
  }
};

const { baseURL: authBaseURL, pathPrefix: authPathPrefix } = getAuthConfig();

// Wrap the native fetch to inject path prefix for Better Auth requests
const originalFetch = window.fetch;
const wrappedFetch = (input: RequestInfo | URL, init?: RequestInit) => {
  let url: string;
  const isRequestInput = input instanceof Request;

  if (typeof input === "string") {
    url = input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else if (input instanceof Request) {
    url = input.url;
  } else {
    url = String(input);
  }

  // Only modify URLs that match our baseURL and need the path prefix
  if (authPathPrefix && url.includes(authBaseURL)) {
    try {
      const urlObj = new URL(url);
      // Check if this is a Better Auth endpoint
      if (
        urlObj.pathname.includes("/api/auth") ||
        urlObj.pathname.match(/\/(get-session|sign-in|sign-out|sign-up)/)
      ) {
        // If the path doesn't already include our prefix, add it
        if (!urlObj.pathname.startsWith(authPathPrefix)) {
          const originalPath = urlObj.pathname;
          urlObj.pathname = `${authPathPrefix}${originalPath}`;
          url = urlObj.toString();
        }
      }
    } catch (e) {
      // If URL parsing fails, continue with original URL
    }
  }

  // Preserve full Request semantics (method/body/headers/etc) when caller passed a Request object.
  // Calling fetch(url, init) would drop Request-specific fields and can degrade to a GET.
  if (isRequestInput) {
    if (url === input.url) {
      return originalFetch(input, init);
    }
    const rewrittenRequest = new Request(url, input);
    return originalFetch(rewrittenRequest, init);
  }

  return originalFetch(url, init);
};

window.fetch = wrappedFetch as typeof fetch;

export const authClient = createAuthClient({
  baseURL: authBaseURL,
  plugins: [
    inferAdditionalFields({
      user: {
        firstname: {
          type: "string",
        },
        lastname: {
          type: "string",
        },
        permissions: {
          type: "string[]",
        },
        organizationId: {
          type: "string",
        },
      },
      session: {
        organizationId: {
          type: "string",
        },
      },
    }),
  ],
  fetchOptions: {
    headers: {
      "x-app-slug": getCurrentAppSlug(),
    },
  },
  advanced: {
    cookiePrefix: "boomer",
  },
  session: {
    useSecureCookies: process.env.NODE_ENV !== "development",
  },
});

/**
 * Sets a cookie to track which app the password reset was initiated from. This enables multi-tenant
 * password reset flow where users can be redirected back to the correct whitelabel app after
 * clicking the email link.
 *
 * The cookie is read by the backend during password reset email generation and included in the
 * email link as a query parameter.
 *
 * @example
 *   ```typescript
 *   // Before calling authClient.forgetPassword()
 *   setPasswordResetAppCookie('custom-app-b');
 *   await authClient.forgetPassword({ email: 'user@example.com' });
 *   ```;
 *
 * @param appSlug - The organization/app slug (e.g., 'boomer', 'custom-app-b')
 */
/**
 * Checks if cookies should use secure attributes based on build environment.
 *
 * @returns True for production builds, false for development
 */
export function isProductionEnvironment(): boolean {
  return import.meta.env.MODE === "production";
}

/**
 * Returns the current app/organization slug.
 *
 * Returns empty string if VITE_ORGANIZATION_SLUG is not set.
 */
export function getCurrentAppSlug(): string {
  return import.meta.env.VITE_ORGANIZATION_SLUG || "";
}

/**
 * Sets a cookie to track the current app/organization slug.
 *
 * Cookie name:
 *
 * - Development: "boomer.app_slug"
 * - Production: "__Secure-boomer.app_slug"
 */
export function setAppSlugCookie(appSlug: string): void {
  const isProduction = isProductionEnvironment();

  const baseCookieName = "boomer.app_slug";
  const cookieName = isProduction ? `__Secure-${baseCookieName}` : baseCookieName;

  const backendUrl = import.meta.env.VITE_BACKEND_URL || "/";
  const domain = getCookieDomainFromBackendUrl(backendUrl);

  // Cookie attributes
  const secureFlag = isProduction ? "; Secure" : "";
  const sameSite = "; SameSite=Lax"; // Lax allows cookie to be sent on navigation from email
  const path = "; Path=/"; // Available across the entire domain
  const maxAge = "; Max-Age=3600"; // 1 hour expiration (matches backend TTL used for reset)
  // Omit Domain attribute for IP addresses (browsers reject it)
  const domainAttr = domain ? `; Domain=${domain}` : "";

  // Build the cookie string
  const cookieString = `${cookieName}=${encodeURIComponent(appSlug)}${path}${maxAge}${sameSite}${domainAttr}${secureFlag}`;

  // Set the cookie
  document.cookie = cookieString;

  // Verify it was set and log for debugging
  const wasSet = document.cookie.includes(cookieName);
  logger.info(
    {
      appSlug,
      isProduction,
      cookieName,
      backendUrl,
      domain,
      cookieString,
      wasSet,
      allCookies: document.cookie,
    },
    "[setAppSlugCookie]",
  );

  if (!wasSet) {
    logger.warn(
      `[setAppSlugCookie] Cookie "${cookieName}" was not set! This might be due to: 1. __Secure- prefix requires HTTPS, 2. SameSite restrictions, 3. Browser security settings`,
    );
  }
}

/**
 * Derives the cookie domain from the Better Auth backend URL.
 *
 * - For normal domains, returns the registrable domain with leading dot e.g.
 *   "https://api.heyboomer.localhost" -> ".heyboomer.localhost"
 * - For IPv4 addresses, returns null (Domain attribute must be omitted for IPs) e.g.
 *   "http://192.168.31.53:3000" -> null
 *
 * @returns The domain string to use in the Domain attribute, or null if Domain should be omitted
 */
export function getCookieDomainFromBackendUrl(backendUrl?: string): string | null {
  const url = backendUrl || import.meta.env.VITE_BACKEND_URL || "/";
  const backendHostname = new URL(url, window.location.origin).hostname;

  const isIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(backendHostname);

  // Browsers reject Domain attribute for IP addresses - must omit it
  if (isIPv4) {
    return null;
  }

  const parts = backendHostname.split(".");
  return parts.length >= 2 ? `.${parts.slice(-2).join(".")}` : backendHostname;
}

/**
 * @deprecated Use setAppSlugCookie instead. This function now delegates to setAppSlugCookie while
 *   preserving the same behavior for existing callers.
 */
export function setPasswordResetAppCookie(appSlug: string): void {
  setAppSlugCookie(appSlug);
}
